import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { Pin } from './Pin';

export type PhaseHeaderProps = {
  /** Typewriter eyebrow, e.g. "PHASE 02 · TOUR DE PAROLE". Rendered uppercase. */
  eyebrow: string;
  /** Big italic display heading, e.g. "Autour de la table". */
  title: string;
  /** Optional italic descriptor in red, e.g. "— un mot, à voix haute, dans l'ordre". */
  descriptor?: ReactNode;
  /** Card tilt in degrees. Defaults to a stable -1.2°/+1.2° pick by `seed`. */
  tilt?: number;
  /** Stable seed for tilt + paper crop. Defaults to `eyebrow`. */
  seed?: string;
  className?: string;
};

const TILTS = [-1.4, -0.8, 0.6, 1.2, -1.1, 1.5];

const hashSeed = (s: string): number => {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h) ^ s.charCodeAt(i);
  return h >>> 0;
};

/**
 * Pinned cream paper-sheet phase header. Top-of-screen recipe used by
 * speaking, debate timer, and vote screens — eyebrow line + italic
 * display heading + red descriptor — with two red pushpins at the top
 * corners.
 */
export const PhaseHeader = ({
  eyebrow,
  title,
  descriptor,
  tilt,
  seed,
  className,
}: PhaseHeaderProps) => {
  const resolvedSeed = seed ?? eyebrow;
  const resolvedTilt = useMemo(() => {
    if (tilt !== undefined) return tilt;
    return TILTS[hashSeed(resolvedSeed) % TILTS.length]!;
  }, [tilt, resolvedSeed]);

  return (
    <div className={['relative px-4 pt-4', className ?? ''].filter(Boolean).join(' ')}>
      <div
        className="paper-dossier relative px-5 pt-4 pb-3"
        style={{ transform: `rotate(${resolvedTilt}deg)` }}
      >
        <Pin left={28} top={-6} />
        <Pin left={'calc(100% - 28px)'} top={-6} />
        <div className="type text-[10px] text-ink/55 uppercase tracking-[0.22em]">{eyebrow}</div>
        <h1
          className="mt-1 font-display font-extrabold text-[32px] text-ink uppercase italic leading-[0.95] sm:text-[40px]"
          style={{ letterSpacing: '-0.01em' }}
        >
          {title}
        </h1>
        {descriptor && (
          <p
            className="mt-1.5 text-[13px] italic leading-[1.35]"
            style={{ color: 'var(--color-stamp-red)' }}
          >
            {descriptor}
          </p>
        )}
      </div>
    </div>
  );
};
